import React from 'react';
import { GraduationCap, Briefcase, Mail, ArrowLeft, ArrowRight } from 'lucide-react';
import { G1, NavyBg, HeroGlow, PAGE_ROOT, OR } from '../../config/styles';
import AboutSection from './AboutSection';
import IntroVideo from '../layout/IntroVideo';

export default function Home({ t, isRTL, onSetPage, settings, lang }) {
  const [logoErr, setLogoErr] = React.useState(false);
  const [hovered, setHovered] = React.useState(null);

  const cards = [
    {
      id: 'academy', label: t.academy, desc: t.academyDesc,
      icon: <GraduationCap size={28} color="white" />,
      grad: 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)',
      accent: '#a5b4fc', glow: 'rgba(99,102,241,0.45)', emoji: '🎓',
    },
    {
      id: 'agency', label: t.agency, desc: t.agencyDesc,
      icon: <Briefcase size={28} color="white" />,
      grad: 'linear-gradient(135deg,#f472b6 0%,#ec4899 100%)',
      accent: '#f9a8d4', glow: 'rgba(236,72,153,0.4)', emoji: '💼',
    },
    {
      id: 'contact', label: t.contact, desc: t.contactDesc,
      icon: <Mail size={28} color="white" />,
      grad: 'linear-gradient(135deg,#10b981 0%,#34d399 100%)',
      accent: '#6ee7b7', glow: 'rgba(16,185,129,0.4)', emoji: '✉️',
    },
  ];

  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div style={{ ...PAGE_ROOT, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', padding: '20px 0 32px' }}>
      <NavyBg />
      <HeroGlow />
      <IntroVideo settings={settings} lang={lang} />

      {/* ── Hero ── */}
      <div style={{ textAlign: 'center', color: 'white', marginBottom: '30px', padding: '0 20px', animation: 'fadeInDown .7s ease both' }}>

        {/* Logo badge */}
        <div style={{ display: 'inline-block', marginBottom: '16px', position: 'relative' }}>
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            borderRadius: '22px', padding: '12px',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 8px 44px rgba(249,115,22,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.14)',
          }}>
            {logoErr
              ? <div style={{ width: 70, height: 70, background: 'linear-gradient(135deg,#f97316,#fb923c)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>💎</div>
              : <img src="/logo.png" alt="logo" onError={() => setLogoErr(true)} style={{ width: 70, height: 70, objectFit: 'contain', borderRadius: '14px', display: 'block' }} />
            }
          </div>
          <div style={{ position: 'absolute', inset: '-7px', borderRadius: '28px', border: '1px solid rgba(249,115,22,0.45)', animation: 'gwPulse 3s ease infinite' }} />
        </div>

        <h1 style={{
          fontSize: 'clamp(30px,5.4vw,52px)', fontWeight: '900',
          margin: '0 0 10px',
          background: 'linear-gradient(135deg,#fff 0%,rgba(255,255,255,0.7) 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          letterSpacing: '-1px', lineHeight: 1.1,
        }}>{t.site}</h1>

        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.72)', maxWidth: '420px', margin: '0 auto', lineHeight: 1.8 }}>
          {t.subtitle}
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: i === 1 ? '22px' : '6px', height: '6px', borderRadius: '3px',
              background: i === 1 ? OR : 'rgba(255,255,255,0.28)', transition: 'all .3s',
            }} />
          ))}
        </div>
      </div>

      {/* ── Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: '18px', maxWidth: '820px', margin: '0 auto', padding: '0 16px', width: '100%' }}>
        {cards.map((item, idx) => {
          const on = hovered === item.id;
          return (
            <div key={item.id}
              onClick={() => onSetPage(item.id)}
              onMouseEnter={() => setHovered(item.id)}
              onMouseLeave={() => setHovered(null)}
              className="gw-card"
              style={{
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: '22px', padding: '28px 20px 22px',
                boxShadow: on ? `0 24px 56px ${item.glow}` : '0 10px 30px rgba(0,0,0,0.3)',
                cursor: 'pointer', textAlign: 'center', position: 'relative', overflow: 'hidden',
                animation: 'cardRise .6s cubic-bezier(.22,1,.36,1) both', animationDelay: `${idx * 0.1 + 0.15}s`,
              }}>

              {/* bg glow blob */}
              <div style={{
                position: 'absolute', top: '-24px', [isRTL ? 'left' : 'right']: '-24px',
                width: '110px', height: '110px', borderRadius: '50%',
                background: item.glow, filter: 'blur(34px)',
                opacity: on ? 0.85 : 0.4, transition: 'opacity .3s',
              }} />

              {/* icon */}
              <div style={{
                width: '60px', height: '60px', background: item.grad, borderRadius: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
                boxShadow: `0 10px 26px ${item.glow}`, transition: 'transform .35s',
                transform: on ? 'scale(1.12) rotate(-4deg)' : 'scale(1)', position: 'relative', zIndex: 1,
              }}>{item.icon}</div>

              <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#fff', marginBottom: '7px', letterSpacing: '-0.3px' }}>
                {item.label}
              </h3>
              <p style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.62)', lineHeight: 1.6, margin: '0 0 16px' }}>
                {item.desc}
              </p>

              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '700',
                color: item.accent, padding: '5px 14px', background: 'rgba(255,255,255,0.06)',
                borderRadius: '20px', border: `1px solid ${item.glow}`, transition: 'all .2s',
              }}>
                {isRTL ? 'اكتشف' : 'Explore'} <Arrow size={12} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── About ── */}
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '0 16px', width: '100%' }}>
        <AboutSection settings={settings} lang={lang || 'ar'} />
      </div>
    </div>
  );
}
